import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { APP_INITIALIZER, Injectable, NgModule, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { SessionService } from '@galaxy/core';
import { Requestor, StorageBackend } from '@openid/appauth';
import { AuthService, Browser, DefaultBrowser } from 'ionic-appauth';
import { XhrSettings } from 'ionic-appauth/lib/cordova';
import { firstValueFrom } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import AuthConfig from './auth.config';
import SecureStorageBackend from './auth/secure-storage';

@Injectable({
  providedIn: 'root',
})
export class NgHttpService implements Requestor {
  private readonly http = inject(HttpClient);

  public async xhr<T>(settings: XhrSettings): Promise<T> {
    if (!settings.method) {
      settings.method = 'GET';
    }

    switch (settings.method) {
      case 'GET':
        return firstValueFrom(this.http.get<T>(settings.url, { headers: this.getHeaders(settings.headers) }));
      case 'POST':
        return firstValueFrom(
          this.http.post<T>(settings.url, settings.data, { headers: this.getHeaders(settings.headers) }),
        );
      case 'PUT':
        return firstValueFrom(
          this.http.put<T>(settings.url, settings.data, { headers: this.getHeaders(settings.headers) }),
        );
      case 'DELETE':
        return firstValueFrom(this.http.delete<T>(settings.url, { headers: this.getHeaders(settings.headers) }));
    }
  }

  private getHeaders(headers): HttpHeaders {
    let httpHeaders: HttpHeaders = new HttpHeaders();

    if (headers !== undefined) {
      Object.keys(headers).forEach((key) => {
        httpHeaders = httpHeaders.append(key, headers[key]);
      });
    }

    return httpHeaders;
  }
}

@NgModule({
  imports: [CommonModule],
  providers: [
    {
      provide: StorageBackend,
      useFactory: () => new SecureStorageBackend(),
    },
    {
      provide: Requestor,
      useClass: NgHttpService,
    },
    {
      provide: Browser,
      useFactory: () => new DefaultBrowser(),
    },
    {
      provide: AuthService,
      useFactory: () => {
        const browser = inject(Browser);
        const storage = inject(StorageBackend);
        const requestor = inject(Requestor);
        const authService = new AuthService(browser, storage, requestor);
        authService.authConfig = inject(AuthConfig);
        return authService;
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const authService = inject(AuthService);
        const sessionService = inject(SessionService);
        return async () => {
          await authService.init();
          authService.token$.subscribe((token) => {
            if (Capacitor.isNativePlatform()) {
              sessionService.setSessionId(token?.accessToken);
            }
          });
        };
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const zone = inject(NgZone);
        const router = inject(Router);
        return async () => {
          App.addListener('appUrlOpen', (data: { url: string }) => {
            if (data.url !== undefined) {
              zone.run(() => {
                const url = new URL(data.url);
                router.navigateByUrl(
                  router.createUrlTree([url.pathname], { queryParams: Object.fromEntries(url.searchParams) }),
                );
              });
            }
          });
        };
      },
      multi: true,
    },
  ],
})
export class AuthModule {}
